import i18n from "i18next";
import { apiGetAsyncTaskStatus } from "../api/lcm";
import SnackbarUtils from "../components/NotistackPlus";
import {
  isApiSuccessCode,
  parseAsyncTaskPollPayload,
} from "../utils/asyncTaskResponse";
import {
  normalizeAsyncTaskStatus,
  startAsyncTaskPoll,
  type PollAsyncTaskHandle,
} from "../utils/asyncTaskPoll";

export interface LifecycleAsyncTaskHandlers {
  onSuccess: (message?: string) => void;
  onFailed: (message?: string) => void;
}

/**
 * App-wide background poll for lifecycle clone tasks (similar to permission polling).
 * Runs silently with no loading UI; stops on LCM sub-app unmount / pagehide.
 */
let pollHandle: PollAsyncTaskHandle | null = null;
let activeAsyncKey: string | null = null;
let hasObservedFirstStatus = false;
let activeHandlers: LifecycleAsyncTaskHandlers | null = null;

export function stopLifecycleAsyncTaskPoll(): void {
  pollHandle?.stop();
  pollHandle = null;
  activeAsyncKey = null;
  hasObservedFirstStatus = false;
  activeHandlers = null;
}

export function startLifecycleAsyncTaskPoll(
  asyncKey: string,
  handlers: LifecycleAsyncTaskHandlers,
): void {
  const taskId = asyncKey.trim();
  if (!taskId) return;

  if (activeAsyncKey === taskId && pollHandle) {
    activeHandlers = handlers;
    return;
  }

  stopLifecycleAsyncTaskPoll();
  activeAsyncKey = taskId;
  activeHandlers = handlers;

  pollHandle = startAsyncTaskPoll({
    fetchStatus: async () => {
      const res = await apiGetAsyncTaskStatus(taskId);
      const parsed = parseAsyncTaskPollPayload(res);
      if (!parsed?.status) {
        if (!isApiSuccessCode(res.code)) return null;
        return null;
      }

      const normalized = normalizeAsyncTaskStatus(parsed.status);
      if (!hasObservedFirstStatus && normalized) {
        hasObservedFirstStatus = true;
        if (normalized === "RUNNING") {
          SnackbarUtils.info(i18n.t("lcm.lifecycleCloneTaskStarted"));
        }
      }

      return parsed;
    },
    onSuccess: ({ message }) => {
      activeHandlers?.onSuccess(message);
    },
    onFailed: ({ message }) => {
      activeHandlers?.onFailed(message);
    },
    onNotFound: () => {},
    onTimeout: () => {},
    onStopped: () => {
      pollHandle = null;
      activeAsyncKey = null;
      hasObservedFirstStatus = false;
      activeHandlers = null;
    },
  });
}
